import { useEffect, useId, useRef, useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'react-hot-toast';

import { Loading } from '@/components/shared';
import { useTimezone } from '@/components/contexts/TimezoneContext';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/NewButton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { SettingRow, SettingsPanel } from '@/components/ui/settings-panel';
import { useCalendarSyncPreferences } from '@/hooks/useCalendarSyncPreferences';
import { apiErrorMessage } from '@/lib/api-client';
import {
  type CalendarSyncPreferences,
  type InviteMembers,
  type ReminderOption,
  type ShowAs,
  type TitleBlock,
  composeOutlookSubject,
  moveTitleBlock,
  SAMPLE_TITLE_VALUES,
  TITLE_BLOCK_LABELS,
  toggleTitleBlock,
} from '@/lib/calendarSyncPreferences';

export function CalendarSyncDefaults() {
  const { data, isLoading, isError, refetch, save, isSaving } = useCalendarSyncPreferences();
  const { timezone } = useTimezone();
  const [prefs, setPrefs] = useState<CalendarSyncPreferences>(data);
  const prefsRef = useRef(prefs);
  prefsRef.current = prefs;

  useEffect(() => {
    setPrefs(data);
    prefsRef.current = data;
  }, [data]);

  const persist = async (patch: Partial<CalendarSyncPreferences>) => {
    const previous = prefsRef.current;
    const body: Partial<CalendarSyncPreferences> = { ...patch };
    if ('all_day' in patch || 'start_time' in patch) {
      body.timezone = timezone;
    }
    const next = { ...previous, ...body };
    prefsRef.current = next;
    setPrefs(next);
    try {
      await save(body);
    } catch (err) {
      if (prefsRef.current === next) {
        prefsRef.current = previous;
        setPrefs(previous);
      }
      toast.error(apiErrorMessage(err) || 'Failed to update Outlook calendar defaults');
    }
  };

  const handleRetry = async () => {
    const result = await refetch();
    if (result.error) {
      toast.error(apiErrorMessage(result.error) || 'Failed to load Outlook calendar defaults');
    }
  };

  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground">
        Defaults used when you sync a deadline to Outlook. They apply the next time you sync.
      </p>
      {isLoading ? (
        <div className="rounded-xl border border-border bg-card px-6 py-5">
          <Loading text="Loading Outlook calendar defaults" />
        </div>
      ) : isError ? (
        <div className="flex flex-col items-start gap-3 rounded-xl border border-border bg-card px-6 py-5">
          <p className="text-sm text-muted-foreground">Outlook calendar defaults could not be loaded.</p>
          <Button
            variant="outline"
            size="sm"
            text="Retry"
            onClick={() => {
              void handleRetry();
            }}
          />
        </div>
      ) : (
        <SettingsPanel>
          <ShowAsRow
            value={prefs.show_as}
            disabled={isSaving}
            onChange={(show_as) => {
              void persist({ show_as });
            }}
          />
          <TimeRow
            allDay={prefs.all_day}
            startTime={prefs.start_time}
            timezone={prefs.timezone}
            disabled={isSaving}
            onCoverageChange={(all_day) => {
              void persist({ all_day, start_time: prefs.start_time });
            }}
            onTimeCommit={(start_time) => {
              void persist({ all_day: false, start_time });
            }}
          />
          <ReminderRow
            value={prefs.reminder}
            disabled={isSaving}
            onChange={(reminder) => {
              void persist({ reminder });
            }}
          />
          <InviteMembersRow
            value={prefs.invite_members}
            disabled={isSaving}
            onChange={(invite_members) => {
              void persist({ invite_members });
            }}
          />
          <TitleBlocksRow
            blocks={prefs.title_blocks}
            disabled={isSaving}
            onChange={(title_blocks) => {
              void persist({ title_blocks });
            }}
          />
        </SettingsPanel>
      )}
    </div>
  );
}

function ShowAsRow({
  value,
  disabled,
  onChange,
}: {
  value: ShowAs;
  disabled: boolean;
  onChange: (value: ShowAs) => void;
}) {
  const id = useId();
  return (
    <SettingRow
      htmlFor={id}
      label="Show as"
      description="Whether the synced event blocks time on your calendar."
    >
      <Select value={value} disabled={disabled} onValueChange={(next) => onChange(next as ShowAs)}>
        <SelectTrigger id={id} className="max-w-xs">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="free">Free</SelectItem>
          <SelectItem value="busy">Busy</SelectItem>
        </SelectContent>
      </Select>
    </SettingRow>
  );
}

function TimeRow({
  allDay,
  startTime,
  timezone,
  disabled,
  onCoverageChange,
  onTimeCommit,
}: {
  allDay: boolean;
  startTime: string;
  timezone: string;
  disabled: boolean;
  onCoverageChange: (allDay: boolean) => void;
  onTimeCommit: (startTime: string) => void;
}) {
  const coverageId = useId();
  const timeId = useId();
  const [draftTime, setDraftTime] = useState(startTime);

  useEffect(() => {
    setDraftTime(startTime);
  }, [startTime]);

  const commitTime = (value: string) => {
    const next = value || '09:00';
    setDraftTime(next);
    if (next !== startTime) onTimeCommit(next);
  };

  return (
    <SettingRow
      htmlFor={coverageId}
      label="Time"
      description="Cover the whole day, or place a 30-minute event at a specific time."
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <Select
          value={allDay ? 'all_day' : 'timed'}
          disabled={disabled}
          onValueChange={(next) => onCoverageChange(next === 'all_day')}
        >
          <SelectTrigger id={coverageId} className="max-w-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all_day">All day</SelectItem>
            <SelectItem value="timed">Specific time</SelectItem>
          </SelectContent>
        </Select>
        {!allDay ? (
          <div className="flex items-center gap-2">
            <Input
              id={timeId}
              type="time"
              value={draftTime}
              aria-label="Event start time"
              className="h-10 max-w-36"
              onChange={(event) => setDraftTime(event.target.value)}
              onBlur={() => commitTime(draftTime)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.currentTarget.blur();
                }
              }}
            />
            <span className="text-xs text-muted-foreground whitespace-nowrap">
              {timezone.replace(/_/g, ' ')}
            </span>
          </div>
        ) : null}
      </div>
    </SettingRow>
  );
}

function ReminderRow({
  value,
  disabled,
  onChange,
}: {
  value: ReminderOption;
  disabled: boolean;
  onChange: (value: ReminderOption) => void;
}) {
  const id = useId();
  return (
    <SettingRow
      htmlFor={id}
      label="Reminder"
      description="Outlook reminder before the deadline."
    >
      <Select
        value={value}
        disabled={disabled}
        onValueChange={(next) => onChange(next as ReminderOption)}
      >
        <SelectTrigger id={id} className="max-w-xs">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="none">No reminder</SelectItem>
          <SelectItem value="1_day">1 day before</SelectItem>
          <SelectItem value="1_week">1 week before</SelectItem>
          <SelectItem value="2_weeks">2 weeks before</SelectItem>
        </SelectContent>
      </Select>
    </SettingRow>
  );
}

function InviteMembersRow({
  value,
  disabled,
  onChange,
}: {
  value: InviteMembers;
  disabled: boolean;
  onChange: (value: InviteMembers) => void;
}) {
  const id = useId();
  return (
    <SettingRow
      htmlFor={id}
      label="Invite matter members"
      description="Outlook sends the event to these people. The person who syncs is the organizer. Recipients receive it without an accept/decline prompt."
    >
      <Select
        value={value}
        disabled={disabled}
        onValueChange={(next) => onChange(next as InviteMembers)}
      >
        <SelectTrigger id={id} className="max-w-xs">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="off">No one</SelectItem>
          <SelectItem value="assignee">Deadline assignee</SelectItem>
          <SelectItem value="all">All matter members</SelectItem>
        </SelectContent>
      </Select>
    </SettingRow>
  );
}

function TitleBlocksRow({
  blocks,
  disabled,
  onChange,
}: {
  blocks: TitleBlock[];
  disabled: boolean;
  onChange: (blocks: TitleBlock[]) => void;
}) {
  const preview = composeOutlookSubject(blocks, SAMPLE_TITLE_VALUES);
  return (
    <SettingRow
      label="Event title"
      description="Turn blocks on or off and reorder them. The short description is a 2–4 word summary of the deadline. Empty values, such as a missing matter number, are skipped."
    >
      <div className="space-y-3">
        <ul className="divide-y divide-border overflow-hidden rounded-lg border border-border">
          {blocks.map((block, index) => (
            <li key={block.key} className="flex items-center gap-2 px-3 py-2">
              <label className="flex min-w-0 flex-1 cursor-pointer items-center gap-3">
                <Checkbox
                  checked={block.enabled}
                  disabled={disabled}
                  onCheckedChange={(checked) =>
                    onChange(toggleTitleBlock(blocks, block.key, checked === true))
                  }
                  aria-label={TITLE_BLOCK_LABELS[block.key]}
                />
                <span className="text-sm text-foreground">{TITLE_BLOCK_LABELS[block.key]}</span>
              </label>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                icon={ChevronUp}
                disabled={disabled || index === 0}
                aria-label={`Move ${TITLE_BLOCK_LABELS[block.key]} up`}
                onClick={() => onChange(moveTitleBlock(blocks, index, -1))}
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                icon={ChevronDown}
                disabled={disabled || index === blocks.length - 1}
                aria-label={`Move ${TITLE_BLOCK_LABELS[block.key]} down`}
                onClick={() => onChange(moveTitleBlock(blocks, index, 1))}
              />
            </li>
          ))}
        </ul>
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Preview</p>
          <p className="mt-1 rounded-md border border-border bg-muted/40 px-3 py-2 text-sm text-foreground">
            {preview}
          </p>
        </div>
      </div>
    </SettingRow>
  );
}
